import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiX, HiClipboard } from "react-icons/hi";

export default function DonationModal({ isOpen, onClose }) {
  const [showForm, setShowForm] = useState(false);
  const [donorInfo, setDonorInfo] = useState({ name: "", email: "", amount: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const bankDetails = {
    accountName: "UpToSkills Tech Foundation",
    accountNumber: "1234567890123456",
    ifscCode: "SBIN0001234",
    bankName: "State Bank of India",
    upiId: "uptoskills@paytm"
  };

  const handleConfirmDonation = async (donorInfo) => {
    const webhookUrl = "https://hook.us2.make.com/cgsn4seby69r0nv17cx03qkvwb79wib2";

    try {
      setIsSubmitting(true);
      const response = await fetch(webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          donorName: donorInfo.name,
          amount: donorInfo.amount,
          email: donorInfo.email,
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        alert("Thank you! Your donation has been recorded for verification.");
        setShowForm(false);
        setDonorInfo({ name: "", email: "", amount: "" });
      }
    } catch (error) {
      console.error("Submission failed:", error);
      alert("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (donorInfo.name && donorInfo.email && donorInfo.amount) {
      handleConfirmDonation(donorInfo);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 lg:p-8 max-w-xs sm:max-w-md lg:max-w-lg xl:max-w-xl w-full shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Support Our Mission
              </h2>
              <button
                onClick={onClose}
                className="p-1 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <HiX className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
              </button>
            </div>

            {/* QR Code Placeholder */}
            <div className="text-center mb-4 sm:mb-6">
              <div className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 mx-auto bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <div className="text-center">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 bg-green-500 rounded-lg mx-auto mb-2 flex items-center justify-center">
                    <span className="text-white font-bold text-xs sm:text-sm">QR CODE</span>
                  </div>
                  <p className="text-xs text-gray-500">Scan to donate via UPI</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                UPI ID: {bankDetails.upiId}
              </p>
            </div>

            {/* Bank Details */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                Bank Transfer Details:
              </h3>
              
              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Account Name</p>
                    <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white break-words">
                      {bankDetails.accountName}
                    </p>
                  </div>
                </div>

                <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 uppercase">Account Number</p>
                    <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white break-all">
                      {bankDetails.accountNumber}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(bankDetails.accountNumber)}
                    className="p-1 sm:p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded ml-2 flex-shrink-0"
                  >
                    <HiClipboard className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                  </button>
                </div>

                <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 uppercase">IFSC Code</p>
                    <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                      {bankDetails.ifscCode}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(bankDetails.ifscCode)}
                    className="p-1 sm:p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded ml-2 flex-shrink-0"
                  >
                    <HiClipboard className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                  </button>
                </div>

                <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Bank Name</p>
                    <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                      {bankDetails.bankName}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-xs sm:text-sm text-green-700 dark:text-green-400 text-center leading-relaxed">
                Your donation supports our work in Education, Health, Environment, Women Empowerment, Rural Development & Entrepreneurship. Thank you! 💚
              </p>
            </div>

            {/* Donation Confirmation Button */}
            <div className="mt-4 sm:mt-6 text-center">
              <button
                onClick={() => setShowForm(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                I've Made a Donation
              </button>
            </div>

            {/* Donation Form */}
            {showForm && (
              <div className="mt-4 sm:mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Confirm Your Donation</h4>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={donorInfo.name}
                    onChange={(e) => setDonorInfo({...donorInfo, name: e.target.value})}
                    className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={donorInfo.email}
                    onChange={(e) => setDonorInfo({...donorInfo, email: e.target.value})}
                    className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                  <input
                    type="number"
                    placeholder="Donation Amount (₹)"
                    value={donorInfo.amount}
                    onChange={(e) => setDonorInfo({...donorInfo, amount: e.target.value})}
                    className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-green-600 text-white py-2 rounded font-semibold hover:bg-green-700 disabled:opacity-50"
                    >
                      {isSubmitting ? "Submitting..." : "Confirm Donation"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}